import { AuthRedirectService } from '@edtech-ui/core/services/auth-redirect/auth-redirect.service';
import { CommonModule } from '@angular/common';
import { Component, ElementRef, QueryList, ViewChildren, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AuthServiceService } from '@edtech-ui/core/services/authService/auth-service.service';
import { OneSignalService } from '@edtech-ui/core/services/one-signal-service/one-signal.service';
import { FireBaseServiceService } from '@edtech-ui/core/services/firebaseService/fire-base-service.service';
import { ToastrService } from 'ngx-toastr';
import { finalize, Subscription } from 'rxjs';

@Component({
	selector: 'app-user-registration-mobile-otp-verify',
	standalone: true,
	imports: [CommonModule, MatProgressSpinnerModule, FormsModule, ReactiveFormsModule, RouterLink],
	templateUrl: './user-registration-mobile-otp-verify.component.html',
	styleUrl: './user-registration-mobile-otp-verify.component.scss'
})
export class UserRegistrationMobileOtpVerifyComponent implements OnInit, OnDestroy {
	@ViewChildren('otpInput') otpInputs!: QueryList<ElementRef<HTMLInputElement>>;

	mobileForm!: FormGroup;
	isLoading = false;
	step: 1 | 2 = 2;
	phone_number!: string;
	resendCooldown = 0;
	private resendTimer?: any;
	firstName!: string;
	lastName!: string;
	phoneEmail!: string;
	collegeName!: string;
	city!: string;
	subscription = new Subscription();
	fcmToken: string = '';
	redirectUrl: string = '';

	constructor(
		private router: Router,
		private route: ActivatedRoute,
		private _toastr: ToastrService,
		private _authService: AuthServiceService,
		private _oneSignalService: OneSignalService,
		private _fireBaseService: FireBaseServiceService,
		private _authRedirectService: AuthRedirectService
	) {
		this.mobileForm = new FormGroup({
			otp: new FormArray(Array.from({ length: 6 }, () => new FormControl('', Validators.required)))
		})
	}

	ngOnInit(): void {
		this.startResendCooldown(120);
		setTimeout(() => this.focusFirstOtp(), 100);
		this.route.queryParams.subscribe(params => {
			this.redirectUrl = params['redirectUrl'] || '';
			this.firstName = params['first_name'];
			this.lastName = params['last_name'];
			this.phone_number = params['phone'];
			this.phoneEmail = params['email'] || '';
			this.collegeName = params['college_name'] || '';
			this.city = params['city'] || '';
		});
		this.fcmToken = this._authService.getFcmToken() || '';
	}

	async verifyOtp() {
		if (!this.isOtpValid()) return;

		let fcmToken = this._authService.getFcmToken() || '';
		if (!fcmToken) {
			try {
				fcmToken = (await Promise.race([
					this._fireBaseService.getFCMToken(),
					new Promise((resolve) => setTimeout(() => resolve(''), 5000))
				])) as string || '';
			} catch (e) {
				console.error('Failed to fetch FCM token', e);
			}
		}
		const playerId = await this._oneSignalService.getPlayerId() || '';

		const payload = {
			first_name: this.firstName,
			last_name: this.lastName,
			phone_num: this.phone_number,
			phone_email: this.phoneEmail,
			college_name: this.collegeName,
			city: this.city,
			otp: Number(this.mobileForm.value.otp.join('')),
			is_default: true,
			registration_source: "web",
			fcm_token: fcmToken,
			onesignal_player_id: playerId,
		} as any;

		this.isLoading = true;
		this.subscription.add(
			this._authService.postNewSignUp(payload).pipe(
				finalize(() => {
					this.isLoading = false;
				})).subscribe({
					next: (response: any) => {

						if (response.Success || response.Status === 200) {
							this._toastr.success(response.Message);

							if (response.Data?.user_id) {
								const newUserId = response.Data.user_id.toString();
								localStorage.setItem('user_id', response.Data.user_id.toString());
								this._authService.currentUserId.next(newUserId);
								this._authService.currentUserId.next(response.Data.user_id.toString());
							}

							if (response.Data?.phone_email) {
								this._authService.currentUserEmail.next(response.Data.phone_email);
								localStorage.setItem('phone_email', response.Data.phone_email);
							}

							if (response.Data?.phone_num) {
								localStorage.setItem('phone_num', response.Data.phone_num);
							}
							localStorage.setItem('is_tenant_onboard', response.Data.is_tenant_onboard.toString());

							this._authService.setToken(response.Data.token);
							this._authService.setModules(response.Data.modules);
							this._authService.setRoleId(response.Data.role_id.toString());

							if (response.Data.refresh_token) {
								localStorage.setItem('refresh_token', response.Data.refresh_token);
							}

							if (response.Data?.modules) {
								this._authService.setModules(response.Data.modules);
							}

							const onboardingDone = response.Data?.onboarding_completed === true;
							localStorage.setItem('onboarding_completed', String(onboardingDone));

							this._authRedirectService.handlePostLoginRedirect(this.redirectUrl);
							// if (!onboardingDone) {
							// 	this.router.navigate(['/onboarding/profile']);
							// } else {
							// 	const landingRoute = this._authService.getFirstAllowedRoute();
							// 	this.router.navigate([landingRoute]);
							// }

						} else {
							this._toastr.error(response.Message);
						}
					},
					error: (err) => {
						this._toastr.error(err?.error?.Message);
					}
				})
		);
	}

	get otpControls(): FormArray {
		return this.mobileForm.get('otp') as FormArray;
	}

	isOtpValid(): boolean {
		return this.otpControls.controls.every(ctrl => ctrl.value);
	}

	resendOtp(): void {

		if (this.resendCooldown > 0) return; 

		const payload = {
			first_name: this.firstName,
			last_name: this.lastName,
			phone_num: this.phone_number,
			phone_email: this.phoneEmail,
			college_name: this.collegeName,
			city: this.city,
			is_default: true,
			registration_source: "web"
		} as any;

		this.isLoading = true;

		this.subscription.add(
			this._authService.postNewSignUp(payload)
				.pipe(finalize(() => this.isLoading = false))
				.subscribe({
					next: (response) => {
						if (response?.Success || response?.Status === 200) {

							this._toastr.success(response?.Message);

							const expiresIn = response?.Data?.expires_in_minutes ?? 2;
							this.startResendCooldown(expiresIn * 60);

							this.otpControls.controls.forEach(ctrl => ctrl.reset());
							this.focusFirstOtp();

						} else {
							this._toastr.error(response?.Message);
						}
					},
					error: (err) => {
						this._toastr.error(err?.error?.Message);
					}
				})
		);
	}

	private startResendCooldown(seconds: number) {
		this.resendCooldown = seconds;

		this.resendTimer && clearInterval(this.resendTimer);

		this.resendTimer = setInterval(() => {
			this.resendCooldown--;

			if (this.resendCooldown <= 0) {
				clearInterval(this.resendTimer);
			}
		}, 1000);
	}

	onOtpInput(event: Event, index: number) {
		const input = event.target as HTMLInputElement;
		const value = input.value;

		if (!/^\d$/.test(value)) {
			input.value = '';
			return;
		}

		if (value && index < this.otpControls.length - 1) {
			const nextInput = input.nextElementSibling as HTMLInputElement;
			nextInput?.focus();
		}
	}

	onOtpKeyDown(event: KeyboardEvent, index: number) {
		if (event.key === 'Backspace' && !this.otpControls.at(index).value && index > 0) {
			const prevInput = (event.target as HTMLInputElement).previousElementSibling as HTMLInputElement;
			prevInput?.focus();
		}
	}

	onOtpPaste(event: ClipboardEvent) {
		event.preventDefault();
		const pastedData = event.clipboardData?.getData('text') || '';

		if (!/^\d{6}$/.test(pastedData)) return;

		pastedData.split('').forEach((digit, index) => {
			if (index < this.otpControls.length) {
				this.otpControls.at(index).setValue(digit);
			}
		});
	}

	private focusFirstOtp(): void {
		const firstInput = this.otpInputs?.first;
		firstInput?.nativeElement.focus();
	}

	editPhoneNumber(): void {
		this.router.navigate(['/mobile-verify']);
	}

	ngOnDestroy(): void {
		this.resendTimer && clearInterval(this.resendTimer);
		this.subscription.unsubscribe();
	}
}
